import { Application } from 'pixi.js';
import type { GameConfig, FPSMetrics } from '../types/game.types';
import { SceneManager } from './SceneManager';
import { logger } from '../utils/logger';

export class GameApplication {
  private app: Application;
  private sceneManager: SceneManager;
  private lastTime: number = 0;
  private fpsMetrics: FPSMetrics = {
    current: 60,
    average: 60,
    min: 60,
    max: 60,
  };
  private fpsHistory: number[] = [];
  private readonly FPS_HISTORY_SIZE = 60;
  private isRunning: boolean = false;

  constructor(config: Partial<GameConfig> = {}) {
    const defaultConfig: GameConfig = {
      width: 1280,
      height: 720,
      backgroundColor: 0x1a1a1a,
      antialias: true,
      resolution: window.devicePixelRatio || 1,
    };

    const finalConfig = { ...defaultConfig, ...config };

    // Create PixiJS application with options (PixiJS v7 synchronous constructor)
    this.app = new Application({
      width: finalConfig.width,
      height: finalConfig.height,
      backgroundColor: finalConfig.backgroundColor,
      antialias: finalConfig.antialias,
      resolution: finalConfig.resolution,
      autoDensity: true,
    });

    // Create scene manager
    this.sceneManager = new SceneManager(this.app);

    logger.info('GameApplication created');
  }

  /**
   * Mount the game canvas to a DOM container.
   */
  mount(container: HTMLElement | string): void {
    const element =
      typeof container === 'string' ? document.querySelector(container) : container;

    if (!element) {
      throw new Error('Container element not found');
    }

    element.appendChild(this.app.view as HTMLCanvasElement);
    logger.info('Game canvas mounted to container');

    // Set up resize handling
    this.setupResizeHandling();

    // Set up visibility handling
    this.setupVisibilityHandling();

    // Initial resize
    this.handleResize();
  }

  /**
   * Start the game loop.
   */
  start(): void {
    if (this.isRunning) {
      logger.warn('Game loop already running');
      return;
    }

    this.isRunning = true;
    this.lastTime = performance.now();
    this.gameLoop();
    logger.info('Game loop started');
  }

  /**
   * Stop the game loop.
   */
  stop(): void {
    this.isRunning = false;
    logger.info('Game loop stopped');
  }

  /**
   * Main game loop with delta time calculation.
   */
  private gameLoop = (): void => {
    if (!this.isRunning) return;

    requestAnimationFrame(this.gameLoop);

    const currentTime = performance.now();
    const deltaMs = currentTime - this.lastTime;
    const delta = deltaMs / 1000; // Convert to seconds

    this.lastTime = currentTime;

    // Update FPS metrics
    this.updateFPS(deltaMs);

    // Update scene manager
    this.sceneManager.update(delta);

    // Render
    this.app.render();
  };

  /**
   * Update FPS metrics for monitoring.
   */
  private updateFPS(deltaMs: number): void {
    const fps = 1000 / deltaMs;

    this.fpsMetrics.current = fps;
    this.fpsHistory.push(fps);

    if (this.fpsHistory.length > this.FPS_HISTORY_SIZE) {
      this.fpsHistory.shift();
    }

    // Calculate average
    const sum = this.fpsHistory.reduce((a, b) => a + b, 0);
    this.fpsMetrics.average = sum / this.fpsHistory.length;

    // Update min/max
    this.fpsMetrics.min = Math.min(...this.fpsHistory);
    this.fpsMetrics.max = Math.max(...this.fpsHistory);

    // Warn if FPS drops below threshold
    if (this.fpsMetrics.current < 55 && import.meta.env.DEV) {
      logger.warn(`Low FPS detected: ${this.fpsMetrics.current.toFixed(1)}`);
    }
  }

  /**
   * Set up window resize handling for responsive canvas.
   */
  private setupResizeHandling(): void {
    window.addEventListener('resize', this.handleResize);
  }

  /**
   * Handle window resize events.
   */
  private handleResize = (): void => {
    const canvas = this.app.view as HTMLCanvasElement;
    const container = canvas.parentElement;
    if (!container) return;

    const width = container.clientWidth;
    const height = container.clientHeight;

    this.app.renderer.resize(width, height);
    this.sceneManager.resize(width, height);

    logger.debug(`Window resized: ${width}x${height}`);
  };

  /**
   * Set up visibility change handling to pause/resume game.
   */
  private setupVisibilityHandling(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        logger.info('Game paused (tab hidden)');
        // Note: We don't stop the loop, just log for now
        // Future: Implement proper pause/resume
      } else {
        logger.info('Game resumed (tab visible)');
        this.lastTime = performance.now(); // Reset time to avoid large delta
      }
    });
  }

  /**
   * Get the scene manager instance.
   */
  getSceneManager(): SceneManager {
    return this.sceneManager;
  }

  /**
   * Get current FPS metrics.
   */
  getFPSMetrics(): FPSMetrics {
    return { ...this.fpsMetrics };
  }

  /**
   * Get the PixiJS application instance.
   */
  getApp(): Application {
    return this.app;
  }

  /**
   * Destroy the application and clean up resources.
   */
  destroy(): void {
    this.stop();
    window.removeEventListener('resize', this.handleResize);
    this.app.destroy(true);
    logger.info('GameApplication destroyed');
  }
}
